import { useNavigate } from 'react-router-dom';
import { Rocket, Lock, ArrowRight, Sparkles, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * LaunchModeCard, 3-state card (locked / almost / unlocked).
 * `compact` renders the dashboard version; full renders inside the hub page.
 */
export default function LaunchModeCard({ data, compact = false, onActivate, onViewRequirements }) {
  const navigate = useNavigate();
  if (!data) return null;
  const { unlocked, required_remaining, percent, next_step } = data;
  const almost = !unlocked && required_remaining <= 2;

  const goJourney = () => navigate(next_step?.href || '/app/launch');
  const goLaunch = () => navigate('/app/launch');

  // Shared shell styles per state
  const shell = unlocked
    ? 'bg-gradient-to-br from-cyan-500/15 via-blue-600/15 to-purple-600/15 border-cyan-400/50 drop-shadow-[0_0_24px_rgba(34,211,238,0.25)]'
    : almost
    ? 'bg-slate-800/60 border-blue-500/40'
    : 'bg-slate-900/70 border-slate-800';

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl border backdrop-blur-xl p-5 sm:p-6 ${shell} ${almost ? 'animate-pulse-slow' : ''}`}
      data-testid="launch-mode-card"
    >
      <div className={`pointer-events-none absolute -top-12 -right-8 w-44 h-44 rounded-full blur-3xl ${unlocked ? 'bg-cyan-500/20' : 'bg-blue-500/10'}`} />
      <div className="relative">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${unlocked ? 'bg-cyan-400/20 text-cyan-300' : 'bg-slate-800 text-slate-300'}`}>
            {unlocked ? <Rocket className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-300">Launch Mode</span>
            <h3 className={`font-outfit font-bold leading-tight ${compact ? 'text-lg' : 'text-2xl'} ${unlocked ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300' : 'text-white'}`} data-testid="launch-mode-title">
              {unlocked ? 'Launch Mode Unlocked' : almost ? "You're Almost Launch Ready" : 'Launch Mode Locked'}
            </h3>
            <p className="text-slate-300 text-sm mt-1.5">
              {unlocked
                ? 'Your FuturePath Launch Pack is ready, clarity becomes action.'
                : almost
                ? `Complete ${required_remaining} more step${required_remaining !== 1 ? 's' : ''} to unlock your personalized Launch Pack.`
                : 'Complete your FuturePath Journey to unlock your personalized Launch Pack.'}
            </p>

            {!unlocked && next_step && (
              <button
                onClick={goJourney}
                className="mt-3 w-full sm:w-auto inline-flex items-center justify-between gap-3 px-3.5 py-2 rounded-xl bg-slate-900/60 border border-slate-700 hover:border-cyan-400/40 text-left transition-colors"
                data-testid="launch-next-step"
              >
                <span className="min-w-0">
                  <span className="block text-[10px] uppercase tracking-wide text-slate-300">Recommended next step</span>
                  <span className="block text-sm text-white truncate">{next_step.label}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-cyan-300 flex-shrink-0" />
              </button>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {unlocked ? (
                <>
                  <button
                    onClick={onActivate || (() => navigate('/app/launch'))}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition drop-shadow-[0_0_14px_rgba(34,211,238,0.5)]"
                    data-testid="activate-launch-mode-btn"
                  >
                    <Sparkles className="w-4 h-4" /> Activate Launch Mode
                  </button>
                  {compact && (
                    <button onClick={() => navigate('/app/launch')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-slate-300 text-sm hover:text-white" data-testid="view-launch-pack-btn">
                      View Launch Pack <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={almost ? goLaunch : goJourney}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold active:scale-95 transition"
                    data-testid="continue-journey-btn"
                  >
                    {almost ? 'Take me to Launch Mode' : 'Continue My Journey'} <ArrowRight className="w-4 h-4" />
                  </button>
                  {compact && (
                    <button onClick={onViewRequirements || (() => navigate('/app/launch'))} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-slate-300 text-sm hover:text-white" data-testid="view-requirements-btn">
                      View Unlock Requirements
                    </button>
                  )}
                  <span className="ml-auto font-mono text-sm text-cyan-300" data-testid="launch-card-percent">{percent}% ready</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
